import axios from 'axios'
import i18n from 'i18n'
import { getBaseURL } from '../config/env'
import history from './history'
import { buildLoginPathWithNext, getCurrentPathWithQuery } from './authRedirect'

const axiosInstance = axios.create({
  baseURL: getBaseURL()
})

const axiosUninterceptedInstance = axios.create({
  baseURL: getBaseURL()
})

export const getTokenPair = () => ({
  accessToken: window && window.localStorage.getItem('grant-connect-access-token'),
  refreshToken: window && window.localStorage.getItem('grant-connect-refresh-token')
})

export const setTokenPair = ({ accessToken, refreshToken }) => {
  accessToken && window && window.localStorage.setItem('grant-connect-access-token', accessToken)
  refreshToken && window && window.localStorage.setItem('grant-connect-refresh-token', refreshToken)
}

export const setIsLibraryMode = isLibraryMode => {
  window && window.localStorage.setItem('grant-connect-library-mode', !!isLibraryMode)
}

export const isLibraryMode = () =>
  window && window.localStorage.getItem('grant-connect-library-mode') === 'true'

export const deleteTokenPair = () => {
  window && window.localStorage.removeItem('grant-connect-access-token')
  window && window.localStorage.removeItem('grant-connect-refresh-token')
}

export const isAuthenticated = () => {
  const { accessToken, refreshToken } = getTokenPair()
  return !!accessToken && !!refreshToken
}

// external state for React to hook into
let backendState = { detectedSuspiciousActivity: false, ipBanned: false }
const backendStateListeners = new Set()

export const backendStore = {
  subscribe(callback) {
    backendStateListeners.add(callback)
    return () => backendStateListeners.delete(callback)
  },
  getSnapshot() {
    return backendState
  },
  notifyListeners() {
    backendStateListeners.forEach(callback => callback())
  },
  clearSuspiciousActivityFlag() {
    backendState = { ...backendState, detectedSuspiciousActivity: false, ipBanned: false }
    backendStore.notifyListeners()
  }
}

const refreshAccessToken = async () => {
  const { refreshToken } = getTokenPair()

  if (refreshToken) {
    try {
      const {
        data: { access: newAccesstoken }
      } = await axiosUninterceptedInstance.post('/api/token/refresh/', {
        refresh: refreshToken
      })
      setTokenPair({ accessToken: newAccesstoken })
    } catch (error) {
      console.error(`Failed refreshing the access token: ${error}`)
      deleteTokenPair()
      history.push(buildLoginPathWithNext(getCurrentPathWithQuery()))
    }
  }
}

axiosInstance.interceptors.request.use(
  config => {
    // set default headers
    config.headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf-8',
      'Accept-Language': i18n.language,
      ...config.headers
    }

    // automatically attach access token if present in local storage
    const { accessToken } = getTokenPair()
    if (accessToken) {
      config.headers['Authorization'] = 'Bearer ' + accessToken
    }

    return config
  },
  err => Promise.reject(err)
)

let refreshPromise = null

axiosInstance.interceptors.response.use(
  response => response,
  async error => {
    let request = error.config

    if (!error.response) {
      return Promise.reject(error)
    }

    if (error.response.status === 401 && !request._retryRefresh) {
      // group token refresh attempts into a single request
      refreshPromise ??= refreshAccessToken().finally(() => {
        refreshPromise = null
      })
      await refreshPromise

      const { accessToken } = getTokenPair()

      if (accessToken) {
        return axiosInstance({
          ...request,
          _retryRefresh: true,
          headers: {
            ...request.headers,
            Authorization: `Bearer ${accessToken}`
          }
        })
      }
    } else if (error.response.status === 429) {
      if (error.response.data?.warning && !request._retryWarning) {
        backendState = { ...backendState, detectedSuspiciousActivity: true, ipBanned: false }
        backendStore.notifyListeners()

        return axiosInstance({
          ...request,
          _retryWarning: true
        })
      } else if (error.response.data?.banned) {
        backendState = { ...backendState, detectedSuspiciousActivity: true, ipBanned: true }
        backendStore.notifyListeners()
        if (isAuthenticated()) {
          deleteTokenPair()
          history.push(buildLoginPathWithNext(getCurrentPathWithQuery()))
        }
      }
    }
    return Promise.reject(error)
  }
)

export default axiosInstance
